import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { TwoNService } from './two-n.service';
import { AirtableService } from './airtable.service';
import { UserProfileCacheService } from './user-profile-cache.service';
import { BuildingStateService } from './building-state.service';
import { WeatherStateService } from './weather-state.service';
import { AnalyticsService } from './analytics.service';
import { PermissionsService } from './permissions.service';
import { PermissionTrackerService } from './permission-tracker.service';
import { Platform } from '@ionic/angular';
import { Subscription, filter, take } from 'rxjs';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { DeviceCacheService } from 'src/mqtt/services/device-cache.service';
import { SettingsPreloadService } from '../pages/settings/services/settings-preload.service';

@Injectable({
  providedIn: 'root'
})
export class AppInitializationService {
  private authSubscription?: Subscription;
  private isInitialized = false;

  constructor(
    private authService: AuthService,
    private twoNService: TwoNService,
    private airtableService: AirtableService,
    private userProfileCacheService: UserProfileCacheService,
    private buildingStateService: BuildingStateService,
    private weatherStateService: WeatherStateService,
    private analyticsService: AnalyticsService,
    private permissionsService: PermissionsService,
    private permissionTrackerService: PermissionTrackerService,
    private platform: Platform,
    private mqttConnectorService: MqttConnectorService,
    private deviceCache: DeviceCacheService,
    private settingsPreloadService: SettingsPreloadService
  ) {
    // Device cache initializes itself in its constructor
    // No need to call initialization here
  }

  // Removed - device cache now initializes itself in constructor

  /**
   * Start monitoring authentication state and initialize services when fully authenticated
   */
  public async startAuthBasedInitialization(): Promise<void> {
    if (this.isInitialized) {
      console.log('App initialization already started');
      return;
    }

    this.isInitialized = true;
    // console.log('Starting authentication-based initialization monitoring');

    // Wait for session restoration to complete before checking auth state
    await this.authService.ensureSessionRestored();

    // Initialize MQTT early if we have a cached IoT token
    this.initializeMqttIfTokenAvailable();

    // Check if user is already fully authenticated (including PIN/FaceID)
    if (this.authService.isFullyAuthenticated()) {
      // console.log('User already fully authenticated, initializing services immediately');
      this.initializeServices();
      return;
    }

    // Check if user has basic Cognito session but needs to complete PIN/FaceID authentication
    const session = this.authService.getSession();
    if (session?.isValid()) {
      // console.log('User has Cognito session but not fully authenticated, monitoring for full authentication');
      // Don't initialize services yet, wait for full authentication
    }

    // Monitor full authentication state changes
    // console.log('User not yet fully authenticated, setting up auth state monitoring');
    this.setupAuthStateMonitoring();
  }

  private setupAuthStateMonitoring(): void {
    // Subscribe to the fully authenticated observable
    this.authSubscription = this.authService.getFullyAuthenticated().subscribe(isFullyAuthenticated => {
      if (isFullyAuthenticated) {
        // console.log('Full authentication detected, initializing services');
        this.initializeServices();
        this.cleanup();
      }
    });

    // Clean up after 60 seconds to avoid infinite monitoring
    setTimeout(() => {
      if (!this.isInitialized) {
        // console.log('Authentication timeout reached, cleaning up monitoring');
        this.cleanup();
      }
    }, 60000);
  }

  private async initializeServices(): Promise<void> {
    // Set RUM user context now (id only) so early page-view events are attributed.
    // Coralogix only tags events fired after this call; enriched with name/email below.
    const earlyUserId = this.authService.getCachedCognitoUserId();
    if (earlyUserId) {
      this.analyticsService.setUserContext(earlyUserId);
    }

    // Schedule non-critical service initialization to run after UI is loaded
    // This prevents blocking the homepage from showing quickly

    setTimeout(async () => {
      // Preload settings data so it's cached before user navigates to settings
      this.settingsPreloadService.preloadAllSettingsData();

      // Initialize building data first
      // console.log('Background: Loading building data...');
      try {
        this.buildingStateService.loadBuilding();
      } catch (error) {
        console.error('Background: Error loading building data:', error);
      }

      // Initialize weather data (depends on building data)
      // console.log('Background: Loading weather data...');
      try {
        this.weatherStateService.loadWeather();
      } catch (error) {
        console.error('Background: Error loading weather data:', error);
      }

      // Initialize user profile cache
      // console.log('Background: Initializing user profile cache...');

      try {
        // Get the current user ID
        const userId = this.authService.getCognitoUserId();
        if (userId) {
          // Set analytics user context. Enrich with name/email when the
          // session is valid; never block init or surface an alert on failure.
          try {
            const userAttributes = await new Promise<Record<string, string> | null>((resolve) => {
              let settled = false;
              const done = (v: Record<string, string> | null) => {
                if (!settled) { settled = true; resolve(v); }
              };
              setTimeout(() => done(null), 3000); // safety: never hang init
              this.authService.getUserAttributes(
                () => done(null),
                (attrs: any[]) => {
                  const map: Record<string, string> = {};
                  (attrs || []).forEach((a: any) => { if (a?.Name) { map[a.Name] = a.Value; } });
                  done(map);
                }
              );
            });
            const userName = userAttributes?.['given_name'] || '';
            const email = userAttributes?.['email'] || '';
            this.analyticsService.setUserContext(userId, userName, email);
          } catch (error) {
            // Set context with just userId if attributes fail
            this.analyticsService.setUserContext(userId);
            console.warn('Background: Could not get user attributes for analytics:', error);
          }

          // Check and report permission statuses
          this.permissionTrackerService.checkAndReportPermissions();

          // Initialize cache from storage
          await this.userProfileCacheService.initializeCache();

          // Refresh profile data in background
          this.userProfileCacheService.refreshUserProfile(userId).then(() => {
            // console.log('Background: User profile cache refreshed successfully');
          }).catch(error => {
            console.error('Background: Error refreshing user profile cache:', error);
          });
        }
      } catch (error) {
        console.error('Background: Error initializing user profile cache:', error);
      }

      // Initialize 2N services on mobile platforms
      if (this.platform.is('ios') || this.platform.is('android')) {
        // console.log('Background: Initializing 2N services post-authentication...');

        this.twoNService.initializeAfterAuth()
          .then(success => {
            if (success) {
              // console.log('Background: 2N services initialized successfully');
            } else {
              console.warn('Background: 2N services initialization failed or skipped');
            }
          })
          .catch(error => {
            console.error('Background: Error initializing 2N services:', error);
          });
      } else {
        // console.log('Background: Skipping 2N initialization on web platform');
      }

      // Request location permission for smart home automations (native only)
      if (this.platform.is('capacitor')) {
        this.permissionsService.requestLocationForSmartHome()
          .then(granted => {
            if (granted) {
              console.log('Background: Location permission granted for smart home');
            } else {
              console.log('Background: Location permission not granted');
            }
          })
          .catch(error => {
            console.error('Background: Error requesting location permission:', error);
          });
      }
    }, 500); // Delay to ensure UI loads first

    // Airtable service is ready to use (no initialization needed)
  }

  /**
   * Initialize MQTT connection early if we have a cached IoT token
   */
  private initializeMqttIfTokenAvailable(): void {
    // First, ensure device cache is initialized with any stored data
    // This must happen IMMEDIATELY so widgets have data when they render
    try {
      this.deviceCache.initializeCachedStates();
    } catch (error) {
      console.error('[AppInit] Error initializing device cache:', error);
    }
    
    // Don't try to connect MQTT here - let the MQTT service handle it
    // based on authentication state and token availability
    // console.log('Device cache initialized, MQTT will connect when fully authenticated');
  }

  private cleanup(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = undefined;
    }
    this.isInitialized = false;
  }
  
  /**
   * Clear user profile cache on logout
   */
  public async clearUserProfileCache(): Promise<void> {
    try {
      await this.userProfileCacheService.clearCache();
    } catch (error) {
      console.warn('Error clearing user profile cache:', error);
    }
    try {
      await this.settingsPreloadService.clearCache();
    } catch (error) {
      console.warn('Error clearing settings cache:', error);
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
} 